import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  makeStyles,
  Chip,
  Grid,
  Paper,
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import {
  getPixelAdDisplayName,
  getLeadAdDisplayName,
  getPixelCampaignName,
  getLeadCampaignName,
  getLeadAdId,
  getWaTrackingMeta
} from "../../utils/pixelCreative";

const getTodaySaoPaulo = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

const useStyles = makeStyles((theme) => ({
  statusBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  adPanel: {
    padding: theme.spacing(2),
    height: "100%",
    minHeight: 120,
    backgroundColor: theme.palette.type === "dark" ? "#1e293b" : "#f0f9ff",
    border: `1px solid ${theme.palette.type === "dark" ? "#334155" : "#bae6fd"}`,
    borderRadius: theme.shape.borderRadius,
  },
  adPanelLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  adPanelName: {
    fontWeight: 600,
    wordBreak: "break-word",
  },
  adPanelId: {
    marginTop: theme.spacing(1),
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    wordBreak: "break-all",
  },
  adPanelSecond: {
    backgroundColor: theme.palette.type === "dark" ? "#1a1f2e" : "#faf5ff",
    border: `1px solid ${theme.palette.type === "dark" ? "#3b3560" : "#ddd6fe"}`,
  },
}));

const PixelPurchaseModal = ({ open, onClose, ticket, pixelTracking, onSuccess }) => {
  const classes = useStyles();
  const [value, setValue] = useState("");
  const [trackedMessage, setTrackedMessage] = useState("");
  const [lookupDate, setLookupDate] = useState(getTodaySaoPaulo());
  const [loading, setLoading] = useState(false);

  const phone = ticket?.contact?.number || pixelTracking?.customerPhone || "";
  const tracking = pixelTracking?.trackingData
    ? JSON.parse(pixelTracking.trackingData)
    : null;
  const missingFields = pixelTracking?.missingFields
    ? JSON.parse(pixelTracking.missingFields)
    : [];
  const creativeName =
    getPixelAdDisplayName(pixelTracking) || getLeadAdDisplayName(tracking);
  const campaignName = getPixelCampaignName(pixelTracking) || getLeadCampaignName(tracking);
  const adId = getLeadAdId(pixelTracking) || getLeadAdId(tracking);
  const waTracking = getWaTrackingMeta(pixelTracking);
  const purchaseCount = tracking?.purchase_count || 0;
  const totalValue = Number(pixelTracking?.saleValue || tracking?.sale_value || 0);

  useEffect(() => {
    if (!open) {
      return;
    }

    setTrackedMessage(pixelTracking?.message || "");

    let date = pixelTracking?.lookupDate || getTodaySaoPaulo();
    if (!pixelTracking?.lookupDate && pixelTracking?.trackingData) {
      try {
        const data = JSON.parse(pixelTracking.trackingData);
        if (data.created_at) {
          date = data.created_at.substring(0, 10);
        }
      } catch (_) {
        // ignore invalid tracking data
      }
    }

    setLookupDate(date);
  }, [open, pixelTracking]);

  const handleSubmit = async () => {
    const numericValue = parseFloat(String(value).replace(",", "."));
    if (!numericValue || numericValue <= 0) {
      toast.error(i18n.t("pixel.modal.invalidValue"));
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post(`/pixel/ticket/${ticket.id}/purchase`, {
        value: numericValue,
      });
      toast.success(
        purchaseCount > 0
          ? i18n.t("pixel.modal.upsellSuccess")
          : i18n.t("pixel.modal.success")
      );
      onSuccess?.(data.pixelTracking);
      setValue("");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    const trimmedMessage = trackedMessage.trim();
    if (!trimmedMessage) {
      toast.error(i18n.t("pixel.manual.messageRequired"));
      return;
    }

    if (!lookupDate) {
      toast.error(i18n.t("pixel.manual.dateRequired"));
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post(`/pixel/ticket/${ticket.id}/refresh`, {
        message: trimmedMessage,
        date: lookupDate,
      });
      onSuccess?.(data.pixelTracking);
      toast.success(i18n.t("pixel.modal.refreshed"));
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = () => {
    if (pixelTracking?.converted && purchaseCount > 0) {
      return (
        <Chip
          size="small"
          color="primary"
          label={i18n.t("pixel.status.purchasesSummary", {
            count: purchaseCount,
            value: totalValue.toFixed(2),
          })}
        />
      );
    }

    if (pixelTracking?.found && pixelTracking?.pixelComplete) {
      return (
        <Chip
          size="small"
          style={{ backgroundColor: "#10b981", color: "#fff" }}
          label={i18n.t("pixel.status.complete")}
        />
      );
    }

    if (pixelTracking?.found) {
      return (
        <Chip
          size="small"
          style={{ backgroundColor: "#f59e0b", color: "#fff" }}
          label={i18n.t("pixel.status.incomplete")}
        />
      );
    }

    return (
      <Chip
        size="small"
        color="secondary"
        label={i18n.t("pixel.status.notFound")}
      />
    );
  };

  const renderAdPanel = () => (
    <Paper className={classes.adPanel} elevation={0}>
      <Typography className={classes.adPanelLabel}>
        {i18n.t("pixel.adLabel")}
      </Typography>
      <Typography variant="body1" className={classes.adPanelName}>
        {creativeName || i18n.t("pixel.adNotFound")}
      </Typography>
      {adId && (
        <Typography className={classes.adPanelId}>
          {i18n.t("pixel.adId")}: {adId}
        </Typography>
      )}
    </Paper>
  );

  const renderCampaignPanel = () => (
    <Paper className={`${classes.adPanel} ${classes.adPanelSecond}`} elevation={0}>
      <Typography className={classes.adPanelLabel}>
        {i18n.t("pixel.campaignLabel")}
      </Typography>
      <Typography variant="body1" className={classes.adPanelName}>
        {campaignName || i18n.t("pixel.campaignNotFound")}
      </Typography>
    </Paper>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("pixel.modal.title")}</DialogTitle>
      <DialogContent>
        <div className={classes.statusBox}>
          {renderStatus()}
          <div className={classes.chipRow}>
            {waTracking ? (
              <Chip
                size="small"
                style={{ backgroundColor: "#059669", color: "#fff" }}
                label={`${i18n.t("pixel.trackingMatched")}: ${waTracking.name}`}
              />
            ) : (
              <Chip
                size="small"
                variant="outlined"
                label={i18n.t("pixel.trackingNotMatched")}
              />
            )}
          </div>
          {tracking?.created_at && (
            <Typography variant="body2">
              <strong>{i18n.t("pixel.modal.leadDate")}:</strong>{" "}
              {tracking.created_at.substring(0, 16).replace("T", " ")}
            </Typography>
          )}
          {pixelTracking?.pixelSentAt && (
            <Typography variant="body2">
              <strong>{i18n.t("pixel.modal.lastPurchase")}:</strong>{" "}
              {new Date(pixelTracking.pixelSentAt).toLocaleString("pt-PT")}
            </Typography>
          )}
          {missingFields.length > 0 && (
            <div className={classes.chipRow}>
              {missingFields.map((field) => (
                <Chip key={field} size="small" variant="outlined" label={field} />
              ))}
            </div>
          )}
        </div>

        <TextField
          fullWidth
          type="date"
          className={classes.field}
          label={i18n.t("pixel.modal.lookupDate")}
          value={lookupDate}
          onChange={(e) => setLookupDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              className={classes.field}
              label={i18n.t("pixel.modal.message")}
              placeholder={i18n.t("pixel.manual.messagePlaceholder")}
              value={trackedMessage}
              onChange={(e) => setTrackedMessage(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            {renderAdPanel()}
          </Grid>
        </Grid>

        <TextField
          fullWidth
          disabled
          className={classes.field}
          label={i18n.t("pixel.modal.phone")}
          value={phone}
        />

        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              label={i18n.t("pixel.modal.value")}
              placeholder="0.00"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            {renderCampaignPanel()}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleRefresh} disabled={loading}>
          {i18n.t("pixel.modal.refresh")}
        </Button>
        <Button onClick={onClose}>{i18n.t("pixel.modal.cancel")}</Button>
        <ButtonWithSpinner
          loading={loading}
          variant="contained"
          color="primary"
          onClick={handleSubmit}
        >
          {purchaseCount > 0
            ? i18n.t("pixel.modal.sendUpsell")
            : i18n.t("pixel.modal.send")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default PixelPurchaseModal;
